import { GameStats } from '../page';

interface StatisticsProps {
  onClose: () => void;
  stats: GameStats;
  gameStatus: 'playing' | 'won' | 'lost';
  targetWord: string;
  onNewGame: () => void;
  darkMode: boolean;
}

export default function Statistics({ onClose, stats, gameStatus, targetWord, onNewGame, darkMode }: StatisticsProps) {
  const winRate = stats.played > 0 ? Math.round((stats.won / stats.played) * 100) : 0;
  const maxGuesses = Math.max(...stats.guessDistribution, 1);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-lg w-full p-6 relative animate-scale-in`}>
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg ${
            darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
          } transition-colors cursor-pointer`}
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <h2 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Statistics</h2>

        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="text-center">
            <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.played}</div>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Played</div>
          </div>
          <div className="text-center">
            <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{winRate}</div>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Win %</div>
          </div>
          <div className="text-center">
            <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.currentStreak}</div>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Current Streak</div>
          </div>
          <div className="text-center">
            <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stats.maxStreak}</div>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Max Streak</div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Guess Distribution</h3>
          <div className="space-y-2">
            {stats.guessDistribution.map((count, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className={`w-4 text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{index + 1}</div>
                <div className="flex-1 flex items-center">
                  <div
                    className="bg-teal-500 text-white text-sm font-semibold px-2 py-1 rounded transition-all duration-300"
                    style={{ width: `${count > 0 ? Math.max((count / maxGuesses) * 100, 8) : 8}%` }}
                  >
                    {count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {gameStatus !== 'playing' && (
          <div className={`p-4 rounded-lg mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            {gameStatus === 'won' ? (
              <p className={`text-center ${darkMode ? 'text-teal-400' : 'text-teal-600'} font-semibold`}>
                🎉 Congratulations! You guessed the word!
              </p>
            ) : (
              <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                The word was: <span className="font-bold text-teal-500">{targetWord}</span>
              </p>
            )}
          </div>
        )}

        <button
          onClick={onNewGame}
          className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          Play New Game
        </button>
      </div>
    </div>
  );
}
